import React from 'react';
import { useHistory } from 'react-router-dom';

import './mobile-search-style.scss';

// components
import Chip from '@src/componentsNew/Chip';
import BackButton from '@src/componentsNew/Buttons/BackButton';
import Typography from '@src/componentsNew/Typography/Typography';
import MobileSearchInput from '@src/componentsNew/Inputs/MobileSearchInput';
import SearchResultItem from '@src/componentsNew/ListItem/SearchResultItem/SearchResultItem';
import RecentlyViewProductListItem from '@src/componentsNew/ListItem/RecentlyViewProductListItem/RecentlyViewProductListItem';

// utils
import { chunkArray } from '@src/utils';
import { goToUrl } from '@src/utils/urlCheck';
import { getInvisibleBrandIds, getInvisibleProductIds } from '@src/utils/constants';
import { useInvisibleProductIds } from "@src/hooks/useInvisibleProductIds";

const MobileSearchPage = (props) => {
  const {
    searchValue,
    recentlyViewedProducts,
    recentlySearchedKeywords,
    mostTrendingKeywords,
    category,
    searchedBrands,
    searchedProducts,
    searchedTags,
    categoryFilter,
    goBack,
    handleClearValue,
    handleSearchInputFocus,
    handleSearchValueChange,
    handleSubmit,
    handleKeywordClick,
    handleClickBrandListItem,
    handleClickCategoryListItem,
    handleClickProductListItem,
    handleClickTagListItem,
  } = props;
  const ready = useInvisibleProductIds();

  const history = useHistory();

  return (
    <div id="MobileSearchWrapper">
      <div className="search-header">
        <BackButton onClick={goBack} />
        <MobileSearchInput
          isSearchPage={true}
          searchValue={searchValue}
          onFocus={handleSearchInputFocus}
          onChange={handleSearchValueChange}
          onSubmit={handleSubmit}
          onClearValue={handleClearValue}
        />
      </div>

      {/* Search Input Value 없을 때 */}
      {searchValue.trim().length === 0 && (
        <section className="empty-search-input-section">
          {recentlyViewedProducts.length ? (
            <div className="recently-view-product-area margin-bt-area">
              <Typography cName="mb-12" variant="subtitle2" component="h3" style={{ color: '#00000099' }}>
                Recently Viewed Products
              </Typography>
              <ul className="recently-view-products-wrapper">
                {recentlyViewedProducts.map((product, i) => (
                  <RecentlyViewProductListItem
                    key={i}
                    thumbnailUrl={product.thumbnailUrl}
                    onClick={() => goToUrl(history, product.urlForGraphql)}
                  />
                ))}
              </ul>
            </div>
          ) : null}

          {/* 최근 검색어 섹션 */}
          {recentlySearchedKeywords.length ? (
            <div className="recently-search-keyword-area">
              <Typography cName="mb-12" variant="subtitle2" component="h3" style={{ color: '#00000099' }}>
                Recently Searched Keywords
              </Typography>
              <div className="recently-search-keywords-wrapper">
                {recentlySearchedKeywords.map((keyword, idx) => (
                  <Chip
                    key={idx}
                    className="recently-search-keyword-item"
                    name={keyword.text}
                    onClick={() => handleKeywordClick(keyword.text)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {/* 인기 검색어 섹션 */}
          {mostTrendingKeywords.length ? (
            <div className="trend-keyword-area">
              <Typography cName="mb-12" variant="subtitle2" component="h3" style={{ color: '#00000099' }}>
                Most Trending Keywords
              </Typography>
              <div className="trend-keywords-wrapper">
                {chunkArray(mostTrendingKeywords, 5).map((trendingKeywords, idx) => (
                  <div key={idx} className="trend-keyword-column-wrapper">
                    {trendingKeywords.subArray.map((trendingKeyword, j) => (
                      <Typography
                        key={j}
                        variant="body3"
                        component="span"
                        onClick={() => handleKeywordClick(trendingKeyword.name)}
                        style={{ color: '#000000DE' }}
                      >
                        {`${idx * 5 + j + 1}. ${trendingKeyword.name}`}
                      </Typography>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      )}

      {/* Search Input Value 있을 때 */}
      {searchValue.trim().length > 0 && (
        <ul className="fill-search-input-section">
          {/* 최근 검색어 3개 */}
          {recentlySearchedKeywords?.length
            ? Array.from(new Set(recentlySearchedKeywords.map((keyword: any) => keyword.text)))
                .filter((text): text is string => typeof text === 'string')
                .map((text) => ({ text }))
                .filter((keyword) => keyword.text.toLowerCase().includes(searchValue.toLowerCase()))
                .slice(0, 3)
                .map((keyword, idx) => (
                  <SearchResultItem
                    key={idx}
                    iconType="time"
                    title={keyword.text}
                    searchQuery={searchValue}
                    onClick={() => handleKeywordClick(keyword.text)}
                  />
                ))
            : null}

          {/* 브랜드 3개 */}
          {searchedBrands?.length
            ? searchedBrands?.filter((brand) => !getInvisibleBrandIds().includes(String(brand._id)))
                  .map((brand, idx) => (
                <SearchResultItem
                  key={idx}
                  iconType="search"
                  title={brand['_source'].name}
                  searchQuery={searchValue}
                  type="Brand"
                  onClick={() => handleClickBrandListItem(brand)}
                />
              ))
            : null}

          {/* 카테고리 3개 */}
          {category.filter(categoryFilter).length
            ? category
                .filter(categoryFilter)
                .slice(0, 3)
                .map((category, idx) => (
                  <SearchResultItem
                    key={idx}
                    iconType="search"
                    title={category['node'].name}
                    searchQuery={searchValue}
                    type="Category"
                    onClick={() => handleClickCategoryListItem(category.node)}
                  />
                ))
            : null}

          {/* 상품 10개 */}
          {searchedProducts?.length && ready
            ? searchedProducts
                ?.filter((product) => !getInvisibleProductIds().includes(product['_id']))
                .map((product, idx) => {
                  const productInfo = product['_source'];

                  return (
                    <SearchResultItem
                      key={idx}
                      imageUrl={productInfo?.image}
                      title={productInfo.name}
                      searchQuery={searchValue}
                      onClick={() => handleClickProductListItem(product)}
                    />
                  );
                })
            : null}

          {/* 태그 10개 */}
          {searchedTags?.length
            ? searchedTags.map((tag, idx) => (
                <SearchResultItem
                  key={idx}
                  iconType="search"
                  title={tag?.first_tag?.hits?.hits[0]['_source'].tag}
                  searchQuery={searchValue}
                  onClick={() => handleClickTagListItem(tag)}
                />
              ))
            : null}
        </ul>
      )}
    </div>
  );
};

export default MobileSearchPage;
